import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import usePageMeta from '../hooks/usePageMeta';
import { BookOpen, ChevronLeft, ChevronRight, List as ListIcon, X, Lock } from 'lucide-react';

// Try public endpoints in order. The real backend exposes `/api/book/content`;
// older builds tried `/api/ebook` and `/api/book/active`, which we keep as fallbacks
// so the page still works if the backend changes.
const BOOK_ENDPOINTS = ['/api/book/content', '/api/ebook', '/api/book/active'];

export default function EbookPage() {
  const [version, setVersion] = useState(null);
  const [err, setErr] = useState(null);
  const [errCode, setErrCode] = useState(null); // 401/403/404/other
  const [chapterIdx, setChapterIdx] = useState(0);
  const [showToc, setShowToc] = useState(true);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: version?.name || 'eBook',
    description: 'Read The Elemental Embrace — chapter by chapter.',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let lastErr = null;
      let lastStatus = null;
      for (const path of BOOK_ENDPOINTS) {
        try {
          const { data } = await api.get(path);
          if (cancelled) return;
          // Normalize: backend sometimes wraps response, sometimes returns version directly.
          const v = data?.version || data;
          if (v && (v.chapters || v.name || v.version_id)) {
            setVersion(v);
            setErr(null);
            setErrCode(null);
            setLoading(false);
            return;
          }
          // If shape is unknown but truthy, still use it.
          if (v) {
            setVersion(v);
            setErr(null);
            setErrCode(null);
            setLoading(false);
            return;
          }
        } catch (e) {
          lastErr = e;
          lastStatus = e?.response?.status || null;
          // For 401/403 we should not keep trying — the user needs access.
          if (lastStatus === 401 || lastStatus === 403) break;
        }
      }
      if (!cancelled) {
        setErr(lastErr?.message || 'Unable to load the book.');
        setErrCode(lastStatus);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const chapters = version?.chapters || [];
  const current = chapters[chapterIdx] || null;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
      </div>
    );
  }

  if (err && !version) {
    // Access denied case
    if (errCode === 401 || errCode === 403) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
          <div className="max-w-md">
            <Lock size={32} className="text-gold mx-auto mb-3" />
            <h2 className="hero-title text-2xl text-white mb-2">Access required</h2>
            <p className="text-ee-muted text-sm mb-5">
              The eBook is part of the full Elemental Embrace journey. Unlock access to begin reading.
            </p>
            <Link to="/purchase" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md gold-grad text-black font-semibold text-sm">
              Get access
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <BookOpen size={32} className="text-gold mx-auto mb-3" />
          <h2 className="hero-title text-2xl text-white mb-2">E-Book is being prepared</h2>
          <p className="text-ee-muted text-sm">The author hasn't activated a book version yet. Check back soon — or chat with EE in the meantime.</p>
          <Link to="/chat" className="inline-block mt-5 text-gold text-sm hover:text-white underline-offset-4 hover:underline">Open EE chat →</Link>
        </div>
      </div>
    );
  }

  if (!version) return null;

  // Empty book state (loaded but no chapters)
  if (chapters.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <BookOpen size={32} className="text-gold mx-auto mb-3" />
          <h2 className="hero-title text-2xl text-white mb-2">{version.name || 'The Book'}</h2>
          <p className="text-ee-muted text-sm">This version has no chapters yet. New content is on the way.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {showToc && (
        <aside className="w-72 bg-ee-bg2 border-r border-ee-border flex-col hidden lg:flex">
          <div className="p-4 border-b border-ee-border flex items-center justify-between">
            <div><div className="text-xs uppercase tracking-wider text-gold/80">Contents</div><div className="text-white font-serif text-lg">{version.name || 'The Book'}</div></div>
            <button onClick={() => setShowToc(false)} className="text-ee-muted hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {chapters.map((ch, i) => (
              <button key={ch.chapter_id || i} onClick={() => setChapterIdx(i)} className={`w-full text-left px-3 py-2 rounded-md text-sm mb-1 transition-colors ${i === chapterIdx ? 'bg-gold/10 text-gold border border-gold/30' : 'text-ee-text2 hover:bg-ee-card2 hover:text-white'}`}>
                <span className="text-xs text-ee-muted mr-2">{String(i + 1).padStart(2, '0')}</span>{ch.title}
              </button>
            ))}
          </div>
        </aside>
      )}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
          <div className="flex items-center justify-between mb-6">
            {!showToc && <button onClick={() => setShowToc(true)} className="text-sm text-ee-muted hover:text-gold inline-flex items-center gap-1"><ListIcon size={14} /> Contents</button>}
            <div className="text-xs text-ee-muted ml-auto">Chapter {chapterIdx + 1} of {chapters.length}</div>
          </div>
          {current ? (
            <article className="prose-ee">
              <div className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-3">Chapter {chapterIdx + 1}</div>
              <h1 className="hero-title text-4xl md:text-5xl text-white mb-8">{current.title}</h1>
              {current.body ? (
                <div className="text-ee-text2 text-lg leading-relaxed whitespace-pre-wrap">{current.body}</div>
              ) : (
                <ul className="space-y-4">
                  {(current.summary_points || []).map((p, i) => (
                    <li key={i} className="flex gap-4 items-start">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      <span className="text-ee-text2 text-lg leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ) : <div className="text-ee-muted">No chapters yet.</div>}
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-ee-border">
            <button disabled={chapterIdx === 0} onClick={() => setChapterIdx((i) => i - 1)} className="inline-flex items-center gap-1 text-sm text-ee-text2 hover:text-gold disabled:opacity-30"><ChevronLeft size={16} /> Previous</button>
            <button disabled={chapterIdx >= chapters.length - 1} onClick={() => setChapterIdx((i) => i + 1)} className="inline-flex items-center gap-1 text-sm text-gold hover:text-white disabled:opacity-30">Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </main>
    </div>
  );
}
